import { UploadOutlined } from '@ant-design/icons';
import { message, Upload } from 'antd';
import imageCompression from 'browser-image-compression';
import React from 'react';
import { usePostHog } from '@posthog/react';
import imageUploadActions from '../actions/imageUpload';
import colors from '../utils/theme/colors';

// UploadButton supports drag-and-drop and button upload for images, with compression and 1MB limit
export const UploadButton = ({ onUpload, ...rest }) => {
  const posthog = usePostHog();
  // Hover state for subtle visual feedback
  const [hovered, setHovered] = React.useState(false);
  // Use browser-image-compression for state-of-the-art, lossless/near-lossless compression
  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      let compressedFile = file;
      // Use browser-image-compression for all images (PNG, JPEG, WebP, etc.)
      // Set options for lossless/near-lossless compression
      const options = {
        maxSizeMB: 10, // Target max size (soft limit)
        maxWidthOrHeight: undefined, // Keep original resolution
        useWebWorker: true,
        fileType: file.type, // Keep original type if possible
        initialQuality: 1, // Highest quality
        alwaysKeepResolution: true,
      };
      try {
        compressedFile = await imageCompression(file, options);
      } catch (e) {
        // If compression fails, fallback to original
        compressedFile = file;
      }
      // Hard check: if still >1MB, show error and abort
      if (compressedFile.size > 10 * 1024 * 1024) {
        message.error('File is larger than 10 MB after compression.');
        onError(new Error('File too large after compression'));
        return;
      }
      const response = await imageUploadActions.uploadImage(compressedFile);
      if (response && response.success && response.image_url) {
        // PostHog: Capture successful image upload
        posthog?.capture('image_uploaded', {
          file_type: file.type,
          file_size_bytes: compressedFile.size,
        });

        onSuccess(response, compressedFile);
        if (onUpload) onUpload(response.image_url);
      } else {
        message.error('Upload failed.');
        onError(new Error('Upload failed'));
      }
    } catch (err) {
      posthog?.capture('image_upload_failed', {
        file_type: file?.type,
        error: err?.message,
      });
      message.error('Upload failed.');
      onError(err);
    }
  };

  // Merge caller styles without losing our base design
  const callerStyle = (rest && rest.style) || {};
  const baseStyle = {
    minHeight: 40,
    padding: '10px 16px',
    borderRadius: 8,
    width: 'fit-content',
    background: hovered ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
    border: hovered
      ? '1px solid rgba(255,255,255,0.2)'
      : '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: colors.muted_foreground,
    userSelect: 'none',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  };

  // Remove style from rest so our merged style applies
  const { style: _ignored, ...restProps } = rest || {};

  return (
    <Upload.Dragger
      customRequest={customRequest}
      showUploadList={false}
      accept='image/*'
      {...restProps}
      multiple={false}
      style={{ ...baseStyle, ...callerStyle }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #1677ff, #722ed1)',
            flexShrink: 0,
          }}
        >
          <UploadOutlined style={{ fontSize: 12, color: '#fff' }} />
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: colors.foreground,
            whiteSpace: 'nowrap',
          }}
        >
          Upload
        </span>
      </div>
    </Upload.Dragger>
  );
};

export default UploadButton;
